import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import isString from 'shared-utils/src/checkVarType/isString';
import makeClassName from 'shared-utils/src/makeClassName';
import { GMAPS_API_KEY } from 'shared-constants/src/gmaps';
import { addGoogleMapsLib } from 'shared-utils/src/googleMapsLib';
import createStyle from 'shared-utils/src/createStyle';
import AutocompleteField from '../AutocompleteField';
import styles, { mainCls } from './styles';

const useStyles = createStyle(styles);

/**
 * Autocomplete input for Google Maps API. Not specified props will be forwarded to the inner `AutocompleteField` component
 */
export default function AutocompleteGoogleMapsField({
  className,
  apiKey,
  inputValue,
  getNameSelected,
  customDisplayName,
  types,
  options,
  bounds,
  widget,
  composedGMInputValue,
  flatSelectedEffect,
  preventBlurFromInput,
  ...props
}) {
  const cls = useStyles();
  const inputRef = useRef(null);
  const [value, setValue] = useState(props.value);
  const [autocomplete, setAutocomplete] = useState(null);
  const [sessionToken, setSessionToken] = useState();
  const componentRestrictions = { country: ['it', 'smr', 'fr', 'mc'] };
  let autocompleteLsr;

  const createGoogleBounds = (bounds = []) => {
    const sw = [
      Math.min(...bounds.map(i => i[0])),
      Math.min(...bounds.map(i => i[1])),
    ];
    const ne = [
      Math.max(...bounds.map(i => i[0])),
      Math.max(...bounds.map(i => i[1])),
    ];

    

    const { maps } = window.google;
    return new maps.LatLngBounds(new maps.LatLng(sw[1], sw[0]), new maps.LatLng(ne[1], ne[0]));
  }

  const loadGm = async () => {
    const loaded = await addGoogleMapsLib({ apiKey: apiKey || GMAPS_API_KEY, version: 3 });
    if (loaded) {
      const input = inputRef.current;
      const { maps } = window.google;
      const { Autocomplete, AutocompleteSessionToken } = await maps.importLibrary('places');
      
      // https://developers.google.com/maps/documentation/javascript/places-autocomplete#session_tokens
      !sessionToken && setSessionToken(new AutocompleteSessionToken());
  
      // If a widget, mount it on render
      if (widget && input) {
        const opts = {
          types,
          componentRestrictions,
          fields: ['address_components', 'geometry.location']
        };
  
        if (bounds && bounds.length) {
          opts.locationRestriction = createGoogleBounds(bounds);
        }
  
        // https://developers.google.com/maps/documentation/javascript/reference/places-widget
        const autocompl = new Autocomplete(input, opts);
        autocompleteLsr = autocompl.addListener('place_changed', () => {
          const place = autocompl.getPlace();
          const addrComp = place ? place.address_components : [];
  
          if (addrComp && addrComp.length) {
            const placeResult = {};
            // const { location } = place.geometry;
            // const latlng = { lat: location.lat(), lng: location.lng() };
            addrComp.forEach(item => {
              const name = item.long_name;
              switch (item.types[0]) {
                case 'street_number':
                  placeResult.streetNumber = name;
                  break;
                case 'route': case 'street_address':
                  placeResult.streetName = name;
                  break;
                case 'locality':
                  placeResult.locality = name;
                  break;
                case 'postal_code':
                  placeResult.postCode = name;
                  break;
                default: break;
              }
            });
            const name = [
              placeResult.streetName,
              placeResult.streetNumber,
              placeResult.locality,
            ].filter(i => i).join(', ');
            const valueRes = inputValue({ id: name, name, placeResult, addrComp, place });
            setValue(valueRes);
            props.onChange && props.onChange(valueRes);
            props.onEnd && props.onEnd();
          }
        });
        // Google library sets 'autocomplete' to 'off' but it doesn't work anymore
        setTimeout(() => input.setAttribute('autocomplete', 'none'), 300);
        setAutocomplete(autocompl);
      }      
    }

    return () => {
      if (autocomplete) {
        maps.event.removeListener(autocompleteLsr);
        maps.event.clearInstanceListeners(autocomplete);
      }
    };
  };
  useEffect(() => { loadGm(); }, []); // eslint-disable-line
  useEffect(() => {
    if (bounds && bounds.length && autocomplete) {
      autocomplete.setBounds(createGoogleBounds(bounds));
      autocomplete.setOptions({ strictBounds: true });
    }
  }, [bounds, autocomplete]); // eslint-disable-line

  const gMapProcess = ({ value }) => new Promise((res) => {
    const { maps } = window.google;
    const service = new maps.places.AutocompleteService();
    // API: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteService.getPlacePredictions

    const opts = {
      sessionToken,
      input: composedGMInputValue
        ? composedGMInputValue(value)
        : inputValue(value),
      componentRestrictions,
      types,
      ...options,
    };

    if (bounds && bounds.length) {
      opts.locationRestriction = createGoogleBounds(bounds);
      // AutocompleteService doesn't have yet the strictBounds option.
      // Issue tracker: https://issuetracker.google.com/issues/36219203
      // opts.strictBounds = true;
    }

    service.getPlacePredictions(opts, pre => {
      res((pre || []).map(item => ({
        ...item,
        id: item.place_id
      })))
    });
  });

  const getDisplayName = item => {
    if (item) {
      return customDisplayName
        ? customDisplayName(item)
        : (item.description || '').replace(', Italia', '');
    }
    return null;
  };

  const nameSelected = getNameSelected ?
    getNameSelected : item => item?.structured_formatting?.main_text || item?.description || '';

  const inputProps = {
    className: makeClassName([
      className,
      cls[mainCls],
      widget ? `${mainCls}--widget` : 0,
    ]),
    getDisplayName,
    getNameSelected: nameSelected,
    dataProcess: gMapProcess,
    postItems: <div className={cls[`${mainCls}__cpy`]} />,
    data: '',
    inputRef,
    value,
    cameFrom: 'googleMaps',
    flatSelectedEffect,
    preventBlurFromInput,
    ...widget && {
      isValid: () => !!value,
      getNameSelected: i => isString(i) ? i : (i.description || i.name || ''),
      getDisplayName: null,
      dataProcess: null,
      emptyState: null,
    },
    ...props,
  };

  return <AutocompleteField {...inputProps} />;
}


AutocompleteGoogleMapsField.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Google API key to use (if not passed a default one will be used)
   */
  apiKey: PropTypes.string,

  /**
   * Customize the input value before submit
   */
  inputValue: PropTypes.func,

  /**
   * Customize the input value
   */
  getNameSelected: PropTypes.func,

  /**
   * Google Maps types to return
   */
  types: PropTypes.array,

  /**
   * Bounds for prediction biasing. This should be an array of geo data, eg. `[[9.2097031, 46.0034609], ... ]`
   */
  bounds: PropTypes.array,

  /**
   * Google Maps custom options
   */
  options: PropTypes.object,

  /**
   * Use Google Maps widget component
   */
  widget: PropTypes.bool,

  composedGMInputValue: PropTypes.func,
  customDisplayName: PropTypes.func,
  flatSelectedEffect: PropTypes.bool,
  preventBlurFromInput: PropTypes.bool,
};

AutocompleteGoogleMapsField.defaultProps = {
  className: '',
  apiKey: '',
  inputValue: i => i,
  getNameSelected: null,
  customDisplayName: null,
  types: ['address'],
  bounds: [],
  options: {},
  widget: false,
  composedGMInputValue: null,
  flatSelectedEffect: false,
  preventBlurFromInput: false,
};